/**
 * ============================================================================
 * LLMValidationDropdown Component
 *
 * LLM 검증 드롭다운 메뉴
 *
 * 지원 LLM:
 * - ChatGPT: 창의적이고 자연스러운 글쓰기
 * - Gemini: 균형잡힌 분석과 제안
 * - Perplexity: 정확한 사실 확인과 검증
 * - Grok: 유머러스하고 트렌디한 스타일
 * - Claude: 형식 엄수·긴 문맥 강점
 * ============================================================================
 */

import { useState, useRef, useEffect } from 'react'
import styles from './LLMValidationDropdown.module.css'

// ============================================================================
// 타입 정의 (LLMServiceType은 llm-prompts에서 import)
// ============================================================================
import type { LLMServiceType } from '@/data/llm-prompts'

interface LLMOption {
  id: LLMServiceType
  name: string
  icon: string
  description: string
}

interface LLMValidationDropdownProps {
  panelType: 'edit' | 'reference'
  onValidate?: (llm: LLMServiceType, panelType: 'edit' | 'reference') => void
  disabled?: boolean
}

// LLM 옵션 목록
const LLM_OPTIONS: LLMOption[] = [
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    icon: '🤖',
    description: '창의적이고 자연스러운 글쓰기',
  },
  {
    id: 'gemini',
    name: 'Gemini',
    icon: '💎',
    description: '균형잡힌 분석과 제안',
  },
  {
    id: 'perplexity',
    name: 'Perplexity',
    icon: '🔍',
    description: '정확한 사실 확인과 검증',
  },
  {
    id: 'grok',
    name: 'Grok',
    icon: '🚀',
    description: '유머러스하고 트렌디한 스타일',
  },
  {
    id: 'claude',
    name: 'Claude',
    icon: '🟣',
    description: '형식 엄수·긴 문맥 강점',
  },
]

// ============================================================================
// LLMValidationDropdown 컴포넌트
// ============================================================================
export function LLMValidationDropdown({
  panelType,
  onValidate,
  disabled = false,
}: LLMValidationDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // LLM 선택 핸들러
  const handleSelectLLM = (llm: LLMServiceType) => {
    if (onValidate) {
      onValidate(llm, panelType)
    }
    setIsOpen(false)
    console.log(`[LLMValidation] ${llm} 선택됨 (패널: ${panelType})`)
  }

  return (
    <div className={styles.llmDropdown} ref={dropdownRef}>
      <button
        type="button"
        className={`${styles.actionButton} ${styles.llmMainBtn}`}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        🔍 LLM 검증 ▼
      </button>

      {isOpen && (
        <div className={styles.llmDropdownMenu}>
          {LLM_OPTIONS.map((llm) => (
            <button
              key={llm.id}
              type="button"
              className={styles.llmOption}
              onClick={() => handleSelectLLM(llm.id)}
            >
              <span className={styles.llmIcon}>{llm.icon}</span>
              <div className={styles.llmInfo}>
                <span className={styles.llmName}>{llm.name}</span>
                <span className={styles.llmDescription}>{llm.description}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default LLMValidationDropdown
